import React, { useState, useEffect } from 'react';
import { X, User, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { User as UserType, UserPermissions } from '../types/user';
import { getUsers, updateUserPermissions, getCurrentUser } from '../lib/localDatabase';

interface UserPermissionsManagerProps {
  onClose: () => void;
}

export const UserPermissionsManager: React.FC<UserPermissionsManagerProps> = ({ onClose }) => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);

  useEffect(() => {
    loadUsers();
    setCurrentUser(getCurrentUser());
  }, []);

  const loadUsers = () => {
    try {
      const allUsers = getUsers().map(user => {
        // Don't expose password
        const { password, ...userWithoutPassword } = user;
        return userWithoutPassword as UserType;
      });
      
      setUsers(allUsers);
      
      // Select first user by default if none selected
      if (allUsers.length > 0 && !selectedUserId) {
        setSelectedUserId(allUsers[0].id);
        setPermissions(allUsers[0].permissions || null);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setStatus({
        type: 'error',
        message: 'Fehler beim Laden der Benutzer'
      });
    }
  };

  const handleUserSelect = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUserId(userId);
      setPermissions(user.permissions || null);
    }
  };

  const handlePermissionChange = (key: keyof UserPermissions, value: boolean) => {
    if (!permissions) return;
    
    setPermissions({
      ...permissions,
      [key]: value
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedUserId || !permissions) return;
    
    try {
      setStatus({ type: null, message: '' });
      
      await updateUserPermissions(selectedUserId, permissions);
      
      // Refresh users list
      loadUsers();
      
      setStatus({
        type: 'success',
        message: 'Berechtigungen wurden erfolgreich aktualisiert'
      });
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setStatus({ type: null, message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error updating permissions:', error);
      setStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Fehler beim Aktualisieren der Berechtigungen'
      });
    }
  };

  const getPermissionLabel = (key: string): string => {
    switch (key) {
      case 'canCreateBuildings': return 'Gebäude erstellen';
      case 'canEditBuildings': return 'Gebäude bearbeiten';
      case 'canDeleteBuildings': return 'Gebäude löschen';
      case 'canManageTechnicians': return 'Techniker verwalten';
      case 'canManageKonzernIds': return 'Konzern IDs verwalten';
      case 'canExportData': return 'Daten exportieren';
      case 'canImportData': return 'Daten importieren';
      case 'canAccessApi': return 'API-Zugriff';
      case 'canViewAuditLogs': return 'Audit-Logs einsehen';
      case 'canManagePermissions': return 'Berechtigungen verwalten';
      default: return key;
    }
  };

  const getPermissionDescription = (key: string): string => {
    switch (key) {
      case 'canCreateBuildings': return 'Erlaubt das Erstellen neuer Gebäude';
      case 'canEditBuildings': return 'Erlaubt das Bearbeiten von Gebäuden, Etagen und Wohnungen';
      case 'canDeleteBuildings': return 'Erlaubt das Löschen von Gebäuden';
      case 'canManageTechnicians': return 'Erlaubt das Hinzufügen, Bearbeiten und Löschen von Technikern';
      case 'canManageKonzernIds': return 'Erlaubt das Hinzufügen, Bearbeiten und Löschen von Konzern IDs';
      case 'canExportData': return 'Erlaubt den Export von Daten (PDF, Excel, etc.)';
      case 'canImportData': return 'Erlaubt den Import von Daten (PDF, Excel, etc.)';
      case 'canAccessApi': return 'Erlaubt den Zugriff auf die API und die Verwaltung von API-Schlüsseln';
      case 'canViewAuditLogs': return 'Erlaubt das Einsehen der Audit-Logs';
      case 'canManagePermissions': return 'Erlaubt das Verwalten von Benutzerberechtigungen';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600 shrink-0" />
            <span className="break-words">Benutzerberechtigungen</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {status.type && (
          <div className={`mb-6 p-4 rounded-lg ${
            status.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-start">
              {status.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mr-2 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 shrink-0" />
              )}
              <p>{status.message}</p>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6">
          {/* User list */}
          <div className="md:w-1/3">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Benutzer</h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <ul className="divide-y divide-gray-200">
                {users.map(user => (
                  <li key={user.id}>
                    <button
                      onClick={() => handleUserSelect(user.id)}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors ${
                        selectedUserId === user.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {user.role === 'admin' ? 'Administrator' : 
                           user.role === 'technician' ? 'Techniker' : 'Betrachter'}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Permissions */}
          <div className="md:w-2/3">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Berechtigungen</h3>
            
            {!selectedUserId ? (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Bitte wählen Sie einen Benutzer aus, um dessen Berechtigungen zu bearbeiten.</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900">
                    {users.find(u => u.id === selectedUserId)?.name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {users.find(u => u.id === selectedUserId)?.email}
                  </p>
                </div>
                
                {permissions ? (
                  <div className="space-y-4">
                    {Object.entries(permissions).map(([key, value]) => (
                      <div key={key} className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id={key}
                            type="checkbox"
                            checked={value}
                            onChange={(e) => handlePermissionChange(key as keyof UserPermissions, e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            disabled={
                              // Prevent removing own permission management rights
                              currentUser?.id === selectedUserId && 
                              key === 'canManagePermissions' && 
                              value === true
                            }
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor={key} className="font-medium text-gray-700">
                            {getPermissionLabel(key)}
                          </label>
                          <p className="text-gray-500">{getPermissionDescription(key)}</p>
                        </div>
                      </div>
                    ))}
                    
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={handleSavePermissions}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Berechtigungen speichern
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">Keine Berechtigungen definiert</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};